// src/services/rapim.ts
import api from './api';
import { RapatPimpinan, PpeppRekomendasi, PpeppProgress, PaginatedResponse } from '../types';

export interface RapimCreatePayload {
  judul: string;
  tanggal: string;
  tempat?: string;
  jenis: 'rutin' | 'insidental' | 'darurat';
  agenda?: string;
  peserta_ids: number[];
  laporan_ids?: number[];
}

export interface RapimUpdatePayload {
  judul?: string;
  tanggal?: string;
  tempat?: string;
  jenis?: 'rutin' | 'insidental' | 'darurat';
  agenda?: string;
  notulen?: string;
  status?: 'dijadwalkan' | 'berlangsung' | 'selesai';
}

export interface PpeppCreatePayload {
  rapat_id: number;
  laporan_masalah_id: number;
  program: string;
  pengendalian: string;
  evaluasi: string;
  pengembangan: string;
  pelaporan: string;
  pic_unit_id: number;
  pic_user_id?: number;
  target_date: string;
  budget?: number;
  priority?: 'rendah' | 'sedang' | 'tinggi' | 'kritis';
}

export interface PpeppProgressPayload {
  progress_percentage: number;
  keterangan: string;
  bukti_url?: string;
}

export interface RapimFilter {
  status?: string;
  jenis?: string;
  bulan?: number;
  tahun?: number;
  month?: number;
  year?: number;
  page?: number;
  per_page?: number;
}

export const rapimService = {
  // === RAPAT PIMPINAN ===

  getAll: async (filters?: RapimFilter) => {
    const res = await api.get('/rapim', { params: filters });
    return res.data as PaginatedResponse<RapatPimpinan>;
  },

  getById: async (id: number) => {
    const res = await api.get(`/rapim/${id}`);
    return res.data.data as RapatPimpinan;
  },

  create: async (data: RapimCreatePayload) => {
    const res = await api.post('/rapim', data);
    return res.data as RapatPimpinan;
  },

  update: async (id: number, data: RapimUpdatePayload) => {
    const res = await api.put(`/rapim/${id}`, data);
    return res.data as RapatPimpinan;
  },

  delete: async (id: number) => {
    await api.delete(`/rapim/${id}`);
  },

  // Start/end rapat
  startRapat: async (id: number) => {
    const res = await api.post(`/rapim/${id}/start`);
    return res.data;
  },

  endRapat: async (id: number) => {
    const res = await api.post(`/rapim/${id}/end`);
    return res.data;
  },

  cancelRapat: async (id: number, alasanPembatalan: string) => {
    const res = await api.post(`/rapim/${id}/cancel`, { alasan_pembatalan: alasanPembatalan });
    return res.data;
  },

  // Notulensi
  saveNotulen: async (id: number, notulen: string) => {
    const res = await api.put(`/rapim/${id}/notulen`, { notulen });
    return res.data;
  },

  // === PESERTA ===

  addPeserta: async (rapatId: number, userIds: number[]) => {
    const res = await api.post(`/rapim/${rapatId}/peserta`, { user_ids: userIds });
    return res.data;
  },

  removePeserta: async (rapatId: number, userId: number) => {
    await api.delete(`/rapim/${rapatId}/peserta/${userId}`);
  },

  updateKehadiran: async (rapatId: number, userId: number, hadir: boolean, keterangan_absensi?: string) => {
    const res = await api.put(`/rapim/${rapatId}/peserta/${userId}/absensi`, { hadir, keterangan_absensi });
    return res.data;
  },

  updateAbsensiBulk: async (rapatId: number, peserta_absensi: { user_id: number; hadir: boolean; keterangan_absensi?: string | null }[]) => {
    const res = await api.put(`/rapim/${rapatId}/peserta/absensi`, { peserta_absensi });
    return res.data;
  },

  // === LAPORAN AGENDA ===

  addLaporan: async (rapatId: number, laporanIds: number[]) => {
    const res = await api.post(`/rapim/${rapatId}/laporan`, { laporan_ids: laporanIds });
    return res.data;
  },

  removeLaporan: async (rapatId: number, laporanId: number) => {
    await api.delete(`/rapim/${rapatId}/laporan/${laporanId}`);
  },

  updateStatusPembahasan: async (rapatId: number, laporanId: number, status: 'pending' | 'dibahas' | 'diputuskan' | 'dibatalkan') => {
    const res = await api.put(`/rapim/${rapatId}/laporan/${laporanId}/status`, { status_pembahasan: status });
    return res.data;
  },

  // Get available laporan for RAPIM (validated_level_2)
  getAvailableLaporan: async () => {
    const res = await api.get('/rapim/available-laporan');
    return res.data;
  },

  // === KEPUTUSAN PPEPP ===

  createKeputusan: async (data: PpeppCreatePayload) => {
    // Backend expects: POST /rapim/{rapat_id}/keputusan
    const res = await api.post(`/rapim/${data.rapat_id}/keputusan`, data);
    return res.data as PpeppRekomendasi;
  },

  updateKeputusan: async (id: number, data: Partial<PpeppCreatePayload>) => {
    const res = await api.put(`/rapim/keputusan/${id}`, data);
    return res.data as PpeppRekomendasi;
  },

  deleteKeputusan: async (id: number) => {
    await api.delete(`/rapim/keputusan/${id}`);
  },

  getKeputusanByRapat: async (rapatId: number) => {
    const res = await api.get(`/rapim/${rapatId}/keputusan`);
    return res.data.data as PpeppRekomendasi[];
  },

  // === PROGRESS PPEPP ===

  addProgress: async (rekomendasiId: number, data: PpeppProgressPayload) => {
    const res = await api.post(`/rapim/keputusan/${rekomendasiId}/progress`, data);
    return res.data as PpeppProgress;
  },

  getProgressHistory: async (rekomendasiId: number) => {
    const res = await api.get(`/rapim/keputusan/${rekomendasiId}/progress`);
    return res.data as PpeppProgress[];
  },

  uploadBuktiProgress: async (rekomendasiId: number, progressId: number, file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    const res = await api.post(
      `/rapim/keputusan/${rekomendasiId}/progress/${progressId}/bukti`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    );
    return res.data;
  },

  // === AGENDA ITEMS ===

  getAgendaItems: async (rapatId: number) => {
    const res = await api.get(`/rapim/${rapatId}/agenda`);
    return res.data.data; // Returns RapimAgendaItem[]
  },

  createAgendaItem: async (rapatId: number, data: {
    title: string;
    description?: string;
    type: 'pembahasan' | 'keputusan' | 'informasi';
    laporan_masalah_id?: number | null;
    duration_minutes?: number;
    order?: number;
  }) => {
    const res = await api.post(`/rapim/${rapatId}/agenda`, data);
    return res.data;
  },

  updateAgendaItem: async (rapatId: number, itemId: number, data: any) => {
    const res = await api.put(`/rapim/${rapatId}/agenda/${itemId}`, data);
    return res.data; // Returns updated RapimAgendaItem
  },

  deleteAgendaItem: async (rapatId: number, itemId: number) => {
    await api.delete(`/rapim/${rapatId}/agenda/${itemId}`);
  },

  reorderAgenda: async (rapatId: number, items: { id: number; order: number }[]) => {
    const res = await api.post(`/rapim/${rapatId}/agenda/reorder`, { items });
    return res.data;
  },

  // === ATTACHMENTS

  uploadAttachment: async (rapatId: number, file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    const res = await api.post(`/rapim/${rapatId}/attachment`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return res.data;
  },

  deleteAttachment: async (rapatId: number, attachmentId: number) => {
    await api.delete(`/rapim/${rapatId}/attachment/${attachmentId}`);
  },

  // === PRESENTASI ===

  getPresentationData: async (rapatId: number) => {
    const res = await api.get(`/rapim/${rapatId}/presentation`);
    return res.data; // Returns optimized structure
  },

  // === EXPORT ===

  exportPDF: async (rapatId: number, template: 'resume' | 'hasil' = 'hasil') => {
    try {
      const res = await api.get(`/rapim/${rapatId}/export/pdf`, {
        params: { template },
        responseType: 'arraybuffer',
      });

      const contentType = res.headers['content-type'];
      if (contentType && contentType.includes('application/json')) {
        const errorData = JSON.parse(new TextDecoder().decode(res.data));
        throw new Error(errorData.message || 'Export failed');
      }

      return res.data;
    } catch (error: any) {
      console.error('❌ Export service error:', error);

      // ✅ Handle ArrayBuffer errors
      if (error.response && error.response.data instanceof ArrayBuffer) {
        try {
          const errorText = new TextDecoder().decode(error.response.data);
          try {
            const errorJson = JSON.parse(errorText);
            throw new Error(errorJson.message || 'Export PDF failed');
          } catch {
            // Not JSON
            console.error('❌ Server HTML Error:', errorText.substring(0, 500));
            throw new Error('Server error - check console for details');
          }
        } catch (decodeError) {
          console.error('❌ Cannot decode error:', decodeError);
        }
      }

      throw error;
    }
  },

  exportNotulen: async (rapatId: number) => {
    try {
      const res = await api.get(`/rapim/${rapatId}/export/notulen`, { responseType: 'arraybuffer' });

      const contentType = res.headers['content-type'];
      if (contentType && contentType.includes('application/json')) {
        const errorData = JSON.parse(new TextDecoder().decode(res.data));
        throw new Error(errorData.message || 'Export failed');
      }

      return res.data;
    } catch (error: any) {
      console.error('Export service error:', error);
      throw error;
    }
  },

  // === STATISTICS ===

  getStats: async (filters?: { bulan?: number; tahun?: number }) => {
    const res = await api.get('/rapim/stats', { params: filters });
    return res.data as {
      total_rapat: number;
      dijadwalkan: number;
      selesai: number;
      total_keputusan: number;
      keputusan_selesai: number;
      avg_progress: number;
    };
  },
};
